import {WinPresentationView} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationView';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {ObjectSize, Position, ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {SymbolsCreation} from 'src/reels/Symbols';
import {WinLine} from '@fantasmagames/slot/src/logic/WinLine';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {ReelsUtils} from '@fantasmagames/slot/src/modules/reels/ReelsUtils';
import {GameReelConstants} from 'src/reels/GameReelConstants';
import {WinNumbers} from '@fantasmagames/slot/src/modules/winpresentation/WinNumbers';

export class GameWinPresentationView extends WinPresentationView {

	public static readonly AFTER_MULTIPLIER_COUNTING_FINISHED: string = 'GameWinPresentationView::AFTER_MULTIPLIER_COUNTING_FINISHED';

	protected addAllSymbolsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		SymbolsCreation.addAllSymbolsIntoSprite(spr, resources);
	}

	protected addBackgroundsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		SymbolsCreation.addBackgroundsIntoSprite(spr, resources);
	}

	protected getSizedSimpleWinAnimationName(isInitial: boolean, size: ObjectSize): string {
		return isInitial ? `INITIAL_WIN_${size.height}` : `INDIVIDUAL_WIN_${size.height}`;
	}

	protected addForegroundIntoSprite(spr: PIXI.Sprite): void {
	}

	protected makeSizedSymbol(symbol: number, size: ObjectSize): number {
		return symbol;
	}

	protected getCurrentWinNumbersPosition(): Position {
		return this.layout.getAbsolutePosition('WIN_NUMBER_POSITION');
	}

	protected setupWinNumbersSettings(): void {
		super.setupWinNumbersSettings();
		this._winNumber.numberAnchor.y = 0.5;
		this._biggerWinNumber.numberAnchor.y = 0.5;
	}

	public playWinPresentationFor(isInitial: boolean, winLines: WinLine[], reels: GameSymbol[][], reelsToSkip?: number[], addLine?: boolean): void {
		this.killCurrentWinPresentation();
		this.hideWinPresentationSymbols();

		this.onInitialWinAnimationFinished();
	}

	protected getAnimationPosition(): SymbolPosition {
		return this.layout.getAbsolutePosition('WIN_POSITION');
	}

	protected getWinSymbolPosition(posOnReels: SymbolPosition, reels: GameSymbol[][]): Position {
		const position: Position = ReelsUtils.getPositionOnReels(posOnReels, reels);

		return {
			x: position.x - GameReelConstants.getBottomSymbolSlot().width / 2,
			y: position.y - GameReelConstants.getBottomSymbolSlot().height / 2,
		};
	}

	protected createWinSymbols(): void {
	}

	public skipCountUp(): void {
		if (this._meter.isPlaying) {
			this._meter.snapToFinalValue();
			this.onWinMeterCompleted();
		}
	}

	public playCountingFormToAfterMultiplier(fromValue: number, toValue: number): void {
		this._currentFinalNumber = toValue;
		this.forceKillWinNumbers();

		this._isPlayingEndAnimation = false;
		this._currentWinNumbers = this._biggerWinNumber;

		this.initMeter();

		this._meter.resolutionPadding = 100;
		this._meter.resolutionWidth = this.resolutionManager.resolution.width;
		this._meter.from(fromValue).to(toValue, this.getCountingTime());
		this._meter.onCompleteCallback = this.onWinMeterCompleted.bind(this);

		this._meter.start();
		this.updateManager.add(this._meter);

		this.killCurrentAnimation();

		this._isEndingAnimationInProgress = false;
		const animPosition: Position = this.getAnimationPosition();

		this.spineUtils.startSpineAnimationCreation('WILD_MULTIPLIER_EFFECT.json', 'EFFECT_01', false)
			.position(animPosition)
			.createAndStart();

		this._currentWinNumberAnimation = this.spineUtils
			.startSpineAnimationCreation('WIN_NUMBERS.json', 'IDLE', true)
			.position(animPosition)
			.createAndStart();

		this.spineUtils.replaceElementInSpineAnimation(this._currentWinNumberAnimation, 'NUMBER', this._currentWinNumbers);

		const zeroNumber = this.currencyFormatter.formatCurrency(fromValue);

		this._currentWinNumbers.showNumber(zeroNumber);
		this._currentWinNumbers.visible = true;
	}

	protected onWinMeterCompleted(): void {
		this.emit(GameWinPresentationView.AFTER_MULTIPLIER_COUNTING_FINISHED);
	}

	public set winTextContainer(value: PIXI.Container) {
		this._winTextContainer = value;

		const winnumberResource = this.getWinnumberResource(this.device.isMobile, false);

		this._winNumber = new WinNumbers(this.resources, winnumberResource);
		this._winNumber.numberAnchor = {x: 0, y: 0.5};
		this._winNumber.numberKerning = -20;
		this.adjustCurrencyOffset(this._winNumber);

		this._winTextContainer.addChild(this._winNumber);
		this._winNumber.visible = false;

		const biggerWinNumberResource = this.getWinnumberResource(this.device.isMobile, true);

		this._biggerWinNumber = new WinNumbers(this.resources, biggerWinNumberResource);
		this._biggerWinNumber.numberAnchor = {x: 0, y: 0.5};
		this._biggerWinNumber.numberKerning = -20;
		this.adjustCurrencyOffset(this._biggerWinNumber);

		this._winTextContainer.addChild(this._biggerWinNumber);
		this._biggerWinNumber.visible = false;

		this.setupWinNumbersSettings();
	}

	protected adjustCurrencyOffset(numbers: WinNumbers): void {
		const currency = this.currencyFormatter.currency;

		if (currency === 'EUR' || currency === 'GBP' || currency === 'MYR' || currency === 'THB'
			|| currency === 'JPY' || currency === 'BOB' || currency === 'CAD' || currency === 'CHF'
			|| currency === 'CNY' || currency === 'COP' || currency === 'ARS' || currency === 'AUD'
			|| currency === 'DKK' || currency === 'GHS' || currency === 'HKD' || currency === 'ILS'
			|| currency === 'KES' || currency === 'MMK' || currency === 'MXN' || currency === 'NOK'
			|| currency === 'NZD' || currency === 'PEN' || currency === 'PHP' || currency === 'RWF'
			|| currency === 'SGD' || currency === 'TWD' || currency === 'ZAR') {
			numbers.currencyOffset = {x: 0, y: 0};
		} else if (currency === 'PKR') {
			numbers.currencyOffset = {x: 0, y: 0};
		} else if (currency === 'PLN' || currency === 'SEK') {
			numbers.currencyOffset = {x: 0, y: 0};
		} else if (currency === 'CZK' || currency === 'HRK' || currency === 'HUF' || currency === 'RUB') {
			numbers.currencyOffset = {x: 0, y: 0};
		} else {
			numbers.currencyOffset = {x: 0, y: 10};
		}
	}

	protected getCountingTime(): number {
		switch (this._currentLevel) {
			case 2:
				return 800;
			case 3:
				return 2300;
			default:
				return 2000;
		}
	}

	protected onMeterFinished(): void {

	}

	public hideWinPresentationSymbols(): void {

	}
}



// WEBPACK FOOTER //
// ./src/winpresentation/GameWinPresentationView.ts